import { useCallback, useRef, useState, useEffect } from 'react';
import type { FieldPlacement } from '@/types/template-fields';
import { CLAIM_FIELD_DEFINITIONS } from '@/types/template-fields';
import { SignatureCanvas } from '@/components/forms/SignatureCanvas';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { X } from 'lucide-react';

interface PlacedFieldProps {
  placement: FieldPlacement;
  isSelected: boolean;
  onSelect: () => void;
  onMove: (x: number, y: number) => void;
  onMoveComplete: (x: number, y: number) => void;
  onResize: (width: number, height: number) => void;
  onResizeComplete: (width: number, height: number) => void;
  onUpdate: (updated: FieldPlacement) => void;
  onDelete: () => void;
  previewMode: boolean;
  containerRef: React.RefObject<HTMLDivElement | null>;
  /** Current visual zoom of the rendered page, so font-size preview matches the box's zoom-relative size. */
  scale: number;
}

export function PlacedField({
  placement,
  isSelected,
  onSelect,
  onMove,
  onMoveComplete,
  onResize,
  onResizeComplete,
  onUpdate,
  onDelete,
  previewMode,
  containerRef,
  scale,
}: PlacedFieldProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const dragStart = useRef({ x: 0, y: 0, startX: 0, startY: 0 });
  const resizeStart = useRef({ x: 0, y: 0, startW: 0, startH: 0 });
  const finalPosition = useRef({ x: placement.x, y: placement.y });
  const finalSize = useRef({ width: placement.width, height: placement.height });
  // Sticky cancel flag: window listeners can fire with stale closures between a
  // pinch-cancel and React detaching them, so state alone can't gate commits.
  const manipulationCancelled = useRef(false);

  const updateDragPosition = useCallback(
    (clientX: number, clientY: number) => {
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();

      const deltaXPct = ((clientX - dragStart.current.x) / rect.width) * 100;
      const deltaYPct = ((clientY - dragStart.current.y) / rect.height) * 100;
      const newX = Math.max(
        0,
        Math.min(100 - placement.width, dragStart.current.startX + deltaXPct),
      );
      const newY = Math.max(
        0,
        Math.min(100 - placement.height, dragStart.current.startY + deltaYPct),
      );
      finalPosition.current = { x: newX, y: newY };
      onMove(newX, newY);
    },
    [containerRef, onMove, placement.height, placement.width],
  );

  const updateResizeDimensions = useCallback(
    (clientX: number, clientY: number) => {
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();

      const deltaWPct = ((clientX - resizeStart.current.x) / rect.width) * 100;
      const deltaHPct = ((clientY - resizeStart.current.y) / rect.height) * 100;

      // Checkboxes are a fixed-size icon, not readable text, so they can shrink
      // much smaller than the general field floor without becoming unusable.
      const isCheckboxField = placement.fieldKey.startsWith('homeowner_checkbox_');
      const minWidth = isCheckboxField ? 2 : 5;
      const minHeight = isCheckboxField ? 1.5 : 2;

      const maxW = Math.min(100, 100 - placement.x);
      const newW = Math.max(minWidth, Math.min(maxW, resizeStart.current.startW + deltaWPct));

      const maxH = Math.min(100, 100 - placement.y);
      const newH = Math.max(minHeight, Math.min(maxH, resizeStart.current.startH + deltaHPct));

      finalSize.current = { width: newW, height: newH };
      onResize(newW, newH);
    },
    [containerRef, onResize, placement.x, placement.y, placement.fieldKey],
  );

  // --- Drag handling ---
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (previewMode || isEditing || showSignaturePad) return;
      e.preventDefault();
      e.stopPropagation();
      onSelect();
      manipulationCancelled.current = false;
      setIsDragging(true);
      dragStart.current = {
        x: e.clientX,
        y: e.clientY,
        startX: placement.x,
        startY: placement.y,
      };
    },
    [placement.x, placement.y, onSelect, previewMode, isEditing, showSignaturePad],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (isDragging) {
        updateDragPosition(e.clientX, e.clientY);
      }

      if (isResizing) {
        updateResizeDimensions(e.clientX, e.clientY);
      }
    },
    [
      isDragging,
      isResizing,
      updateDragPosition,
      updateResizeDimensions,
    ],
  );

  // A second finger means the user is pinch-zooming the document (handled by
  // usePdfPageRenderer); abandon the drag/resize and revert to the pre-gesture
  // placement without committing, so the zoom can't smear the field around.
  const cancelManipulation = useCallback(() => {
    manipulationCancelled.current = true;
    if (isDragging) {
      finalPosition.current = { x: dragStart.current.startX, y: dragStart.current.startY };
      onMove(dragStart.current.startX, dragStart.current.startY);
    }
    if (isResizing) {
      finalSize.current = { width: resizeStart.current.startW, height: resizeStart.current.startH };
      onResize(resizeStart.current.startW, resizeStart.current.startH);
    }
    setIsDragging(false);
    setIsResizing(false);
  }, [isDragging, isResizing, onMove, onResize]);

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (manipulationCancelled.current || (!isDragging && !isResizing)) return;
      if (e.touches.length > 1) {
        cancelManipulation();
        return;
      }
      const touch = e.touches[0];
      if (!touch) return;
      e.preventDefault();

      if (isDragging) {
        updateDragPosition(touch.clientX, touch.clientY);
      }

      if (isResizing) {
        updateResizeDimensions(touch.clientX, touch.clientY);
      }
    },
    [isDragging, isResizing, cancelManipulation, updateDragPosition, updateResizeDimensions],
  );

  const handleMouseUp = useCallback(() => {
    if (!manipulationCancelled.current) {
      if (isDragging) {
        onMoveComplete(finalPosition.current.x, finalPosition.current.y);
      }
      if (isResizing) {
        onResizeComplete(finalSize.current.width, finalSize.current.height);
      }
    }
    setIsDragging(false);
    setIsResizing(false);
  }, [isDragging, isResizing, onMoveComplete, onResizeComplete]);

  useEffect(() => {
    if (isDragging || isResizing) {
      // Catch the pinch at the second finger's touchstart, before any move.
      const handleWindowTouchStart = (e: TouchEvent) => {
        if (e.touches.length > 1) cancelManipulation();
      };
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchstart', handleWindowTouchStart);
      window.addEventListener('touchmove', handleTouchMove, { passive: false });
      window.addEventListener('touchend', handleMouseUp);
      window.addEventListener('touchcancel', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('touchstart', handleWindowTouchStart);
        window.removeEventListener('touchmove', handleTouchMove);
        window.removeEventListener('touchend', handleMouseUp);
        window.removeEventListener('touchcancel', handleMouseUp);
      };
    }
  }, [isDragging, isResizing, handleMouseMove, handleMouseUp, handleTouchMove]);

  // --- Resize handle ---
  const handleResizeMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (previewMode || showSignaturePad) return;
      e.preventDefault();
      e.stopPropagation();
      manipulationCancelled.current = false;
      setIsResizing(true);
      resizeStart.current = {
        x: e.clientX,
        y: e.clientY,
        startW: placement.width,
        startH: placement.height,
      };
    },
    [placement.width, placement.height, previewMode, showSignaturePad],
  );

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (previewMode || isEditing || showSignaturePad) return;
      if (e.touches.length > 1) return;
      const touch = e.touches[0];
      if (!touch) return;
      e.preventDefault();
      e.stopPropagation();
      onSelect();
      manipulationCancelled.current = false;
      setIsDragging(true);
      dragStart.current = {
        x: touch.clientX,
        y: touch.clientY,
        startX: placement.x,
        startY: placement.y,
      };
    },
    [isEditing, onSelect, placement.x, placement.y, previewMode, showSignaturePad],
  );

  const handleResizeTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (previewMode || showSignaturePad) return;
      if (e.touches.length > 1) return;
      const touch = e.touches[0];
      if (!touch) return;
      e.preventDefault();
      e.stopPropagation();
      manipulationCancelled.current = false;
      setIsResizing(true);
      resizeStart.current = {
        x: touch.clientX,
        y: touch.clientY,
        startW: placement.width,
        startH: placement.height,
      };
    },
    [placement.height, placement.width, previewMode, showSignaturePad],
  );

  // Auto-focus textarea when entering edit mode
  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      // Move cursor to end
      const len = textareaRef.current.value.length;
      textareaRef.current.setSelectionRange(len, len);
    }
  }, [isEditing]);

  const isStaticText = placement.type === 'static_text';
  const isAdjusterSig = placement.type === 'adjuster_signature';
  const isCustomField = placement.type === 'text_input';
  const isSignatureField = placement.type === 'signature';
  const isInitialsField = placement.fieldKey.startsWith('homeowner_initials_');
  const isLongTextField = placement.fieldKey.startsWith('homeowner_long_text_');
  const isNumberField = placement.fieldKey.startsWith('homeowner_number_');
  const isDateField = placement.fieldKey.startsWith('homeowner_date_');
  const isCheckboxField = placement.fieldKey.startsWith('homeowner_checkbox_');
  const isInlineEditable = isStaticText || isAdjusterSig;
  const shouldWrapText = isStaticText || (placement.multiline ?? isLongTextField);

  // Display value based on field type
  const fieldDef = CLAIM_FIELD_DEFINITIONS.find(
    (f) => f.fieldKey === placement.fieldKey,
  );
  let displayValue: string;
  if (isStaticText) {
    displayValue = placement.staticContent || (previewMode ? '' : 'Double-click to type...');
  } else if (isAdjusterSig) {
    displayValue = placement.staticContent
      ? ''
      : (previewMode ? '' : 'Double-click to sign...');
  } else if (isCustomField) {
    if (isCheckboxField) {
      displayValue = 'X';
    } else if (!previewMode) {
      displayValue = `{{${placement.label}}}`;
    } else if (isInitialsField) {
      displayValue = 'JD';
    } else if (shouldWrapText) {
      displayValue = 'Example multiline\nresponse';
    } else if (isNumberField) {
      displayValue = '123';
    } else if (isDateField) {
      displayValue = '9/15/2026';
    } else {
      displayValue = 'Example text';
    }
  } else if (isSignatureField) {
    displayValue = previewMode ? 'Signature' : `{{${placement.label}}}`;
  } else {
    displayValue = previewMode
      ? fieldDef?.exampleValue ?? placement.label
      : `{{${placement.label}}}`;
  }

  // Handle double-click to enter inline edit
  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    if (previewMode || !isInlineEditable) return;
    e.stopPropagation();
    e.preventDefault();
    if (isStaticText) {
      setIsEditing(true);
    } else if (isAdjusterSig) {
      setShowSignaturePad(true);
    }
  }, [previewMode, isInlineEditable, isStaticText, isAdjusterSig]);

  return (
    <div
      className={`absolute group ${previewMode ? '' : (isEditing ? 'cursor-text' : 'cursor-move')} ${
        isSelected && !previewMode ? 'ring-2 ring-primary z-20' : 'z-10'
      } ${isDragging ? 'opacity-80' : ''}`}
      style={{
        left: `${placement.x}%`,
        top: `${placement.y}%`,
        width: `${placement.width}%`,
        height: `${placement.height}%`,
      }}
      onTouchStart={handleTouchStart}
      onMouseDown={handleMouseDown}
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
      onDoubleClick={handleDoubleClick}
    >
      {/* Field content */}
      <div
        ref={contentRef}
        className={`w-full h-full overflow-hidden px-1 ${
          isEditing ? 'flex' : 'flex items-center'
        } ${
          previewMode
            ? isStaticText
              ? 'bg-transparent'
              : isAdjusterSig
              ? 'bg-emerald-50 border border-emerald-200 rounded'
              : isSignatureField
              ? 'bg-transparent border-transparent'
              : isCustomField
              ? 'bg-transparent border-transparent'
              : 'bg-transparent'
            : isStaticText
            ? (isEditing
              ? 'bg-amber-50 border border-amber-400 rounded-sm'
              : 'bg-amber-100/30 border border-amber-400/60 border-dashed rounded-sm')
            : isAdjusterSig
            ? 'bg-emerald-100/30 border border-emerald-400/60 border-dashed rounded-sm'
            : isSignatureField
            ? 'bg-purple-100/30 border border-purple-400/60 border-dashed rounded-sm'
            : isCustomField
            ? 'bg-blue-100/30 border border-blue-400/60 border-dashed rounded-sm'
            : 'bg-primary/10 border border-primary/40 border-dashed rounded-sm'
        }`}
        style={{
          fontSize: `${placement.fontSize * scale}px`,
          fontFamily: placement.fontFamily,
          textAlign: placement.textAlign,
          touchAction: previewMode ? 'auto' : 'none',
        }}
      >
        {/* Inline textarea for static_text when editing */}
        {isStaticText && isEditing && !previewMode ? (
          <textarea
            ref={textareaRef}
            className="w-full h-full bg-transparent border-none outline-none resize-none text-amber-900 p-0 m-0"
            style={{
              fontSize: `${placement.fontSize * scale}px`,
              fontFamily: placement.fontFamily,
              textAlign: placement.textAlign,
            }}
            value={placement.staticContent || ''}
            onChange={(e) => {
              onUpdate({ ...placement, staticContent: e.target.value });
            }}
            onBlur={() => setIsEditing(false)}
            onKeyDown={(e) => {
              // Allow Escape to exit editing
              if (e.key === 'Escape') {
                setIsEditing(false);
              }
              // Stop propagation so Delete/Backspace don't trigger field deletion
              e.stopPropagation();
            }}
            onMouseDown={(e) => e.stopPropagation()}
            placeholder="Type your text here..."
          />
        ) : isAdjusterSig && placement.staticContent ? (
          /* Show saved adjuster signature image */
          <img
            src={placement.staticContent}
            alt="Your signature"
            className="w-full h-full object-contain"
          />
        ) : isCheckboxField ? (
          /* Real checkbox square (matches the homeowner-facing view) instead of a text placeholder */
          <div className="w-full h-full flex items-center justify-center">
            <Checkbox
              checked={previewMode}
              disabled
              className="pointer-events-none rounded-none"
              style={{
                height: `${Math.max(12, 16 * scale)}px`,
                width: `${Math.max(12, 16 * scale)}px`,
              }}
            />
          </div>
        ) : (
          <span
            className={`w-full ${shouldWrapText ? 'whitespace-pre-wrap break-words' : 'whitespace-nowrap'} ${
              previewMode
                ? isStaticText
                  ? 'text-foreground'
                  : isAdjusterSig
                  ? 'text-emerald-600 italic'
                  : isSignatureField
                  ? 'text-foreground'
                  : isCustomField
                  ? 'text-foreground'
                  : 'text-foreground'
                : isStaticText
                ? (placement.staticContent ? 'text-amber-900' : 'text-amber-400 italic')
                : isAdjusterSig
                ? 'text-emerald-400 italic'
                : isSignatureField
                ? 'text-purple-600'
                : isCustomField
                ? 'text-blue-600'
                : 'text-primary'
            }`}
          >
            {displayValue}
          </span>
        )}
      </div>

      {/* Delete button */}
      {isSelected && !previewMode && !isEditing && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className="absolute -top-2 -right-2 h-6 w-6 sm:h-5 sm:w-5 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center hover:bg-destructive/90 shadow-sm z-30"
        >
          <X className="h-3 w-3" />
        </button>
      )}

      {/* Resize handle */}
      {isSelected && !previewMode && !isEditing && (
        <div
          className="absolute -bottom-1 -right-1 h-4 w-4 sm:h-3 sm:w-3 bg-primary rounded-sm cursor-se-resize z-30"
          onMouseDown={handleResizeMouseDown}
          onTouchStart={handleResizeTouchStart}
        />
      )}

      {/* Signature dialog for adjuster_signature */}
      <Dialog open={showSignaturePad && isAdjusterSig && !previewMode} onOpenChange={(open) => !open && setShowSignaturePad(false)}>
        <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Draw Your Signature</DialogTitle>
          </DialogHeader>
          <SignatureCanvas
            onSave={(dataUrl) => {
              onUpdate({ ...placement, staticContent: dataUrl });
              setShowSignaturePad(false);
            }}
            onCancel={() => setShowSignaturePad(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
